import { Box, Card, Stack } from '@mui/material';
import {
  AI_AGENTS_INFO,
  AiAgentWithFacts,
  AgentTypeWithFake,
  FakeAgentType,
  FakeAgentTypeKey,
  UserSupportAgentFact,
} from './aiAgentsConsts';
import { Button, colors, Typography } from '@sweep-io/sweep-design';
import { Chat } from '@sweep-io/sweep-design/dist/icons';
import AgentCardMenu from './AgentCardMenu';
import { useState } from 'react';
import ChatIconDocumentation from './svg/facts/chatIconDocumentation.svg?react';
import UserIconDocumentation from './svg/facts/userIconDocumentation.svg?react';
import UserIconPermissions from './svg/facts/userIconPermissions.svg?react';
import ChatIconPermissions from './svg/facts/chatIconPermissions.svg?react';
import MegaphoneIconMonitoring from './svg/facts/megaphoneIconMonitoring.svg?react';
import FunnelsIconProcess from './svg/facts/funnelsIconProcess.svg?react';
import CubeIconMonitoring from './svg/facts/cubeIconMonitoring.svg?react';
import { telemetry } from '../../telemetry';
import { useSelector } from 'react-redux';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import useObjectTypesWithFetch from '../../hooks/useObjectTypesWithFetch';
import {
  AgentType,
  AgentFactKey,
  TotalChatsFact,
  TotalUserChatsFact,
  FunnelNamesFact,
  TotalIssuesFact,
  ObjectTypeFact,
} from '@server/ai';
import ChatsIcon from './userSupport/Chats.svg?react';
import ResolveIcon from './userSupport/Resolved.svg?react';
import pluralize from 'pluralize';
import { isMetadataTypeAgent } from './helper';

const getFactUi = (
  agentType: AgentTypeWithFake,
  fact:
    | TotalChatsFact
    | TotalUserChatsFact
    | FunnelNamesFact
    | TotalIssuesFact
    | ObjectTypeFact
    | UserSupportAgentFact,
) => {
  switch (agentType) {
    case AgentType.Documentation:
      switch (fact.key) {
        case AgentFactKey.TOTAL_CHATS:
          return {
            icon: <ChatIconDocumentation />,
            text: `${fact.value} conversations`,
          };
        case AgentFactKey.TOTAL_USER_CHATS:
          return { icon: <UserIconDocumentation />, text: `${fact.value} started by you` };
        default: {
          telemetry.captureError('No data found for this fact key', fact.key);
          return;
        }
      }
    case AgentType.Permissions:
      switch (fact.key) {
        case AgentFactKey.TOTAL_CHATS:
          return { icon: <ChatIconPermissions />, text: `${fact.value} conversations` };
        case AgentFactKey.TOTAL_USER_CHATS:
          return { icon: <UserIconPermissions />, text: `${fact.value} started by you` };
        default: {
          telemetry.captureError('No data found for this fact key', fact.key);
          return;
        }
      }
    case AgentType.MonitoringV2:
      switch (fact.key) {
        case AgentFactKey.TOTAL_ISSUES:
          return { icon: <MegaphoneIconMonitoring />, text: `${fact.value} active issues` };
        case AgentFactKey.OBJECT_TYPE:
          return { icon: <CubeIconMonitoring />, text: `Object: ${fact.value}` };
        default: {
          telemetry.captureError('No data found for this fact key', fact.key);
          return;
        }
      }
    case AgentType.ProcessOptimization:
      switch (fact.key) {
        case AgentFactKey.FUNNEL_NAMES:
          if (Array.isArray(fact.value)) {
            return { icon: <FunnelsIconProcess />, text: `Working on: ${fact.value.join(', ')}` };
          } else {
            telemetry.captureError('No data found for this fact value', fact.value);
            return;
          }
        default: {
          telemetry.captureError('No data found for this fact key', fact.key);
          return;
        }
      }

    case FakeAgentType.SupportAgent:
      switch (fact.key) {
        case FakeAgentTypeKey.OPEN_CONVERSATIONS:
          return {
            icon: <ChatsIcon />,
            text: `${fact.value} open ${pluralize('issue', fact.value)}`,
          };

        case FakeAgentTypeKey.RESOLVED_CONVERSATIONS:
          return {
            icon: <ResolveIcon />,
            text: `${fact.value} resolved by admin`,
          };

        default: {
          telemetry.captureError('No data found for this fact key', fact.key);
          return;
        }
      }
    default: {
      telemetry.captureError('No data found for this agent type', agentType);
    }
  }
};

const AgentCard = ({
  agent,
  onClick,
  opacity = 1,
  ctaText = 'New chat',
  hideActionButtons,
}: {
  agent: AiAgentWithFacts;
  onClick?: () => void;
  opacity?: number;
  ctaText?: string;
  hideActionButtons?: boolean;
}) => {
  const { name, type, facts } = agent;
  const { name: typeName } = AI_AGENTS_INFO[type];
  const [isHovered, setIsHovered] = useState(false);
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { objectTypesByName } = useObjectTypesWithFetch({ crmOrgId: crmOrgId ?? '' });

  const totalChatsFact = facts.find((fact) => fact.key === AgentFactKey.TOTAL_CHATS);
  const shouldShowNoConversations = totalChatsFact && totalChatsFact.value === 0;

  return (
    <Card
      onClick={onClick}
      sx={{
        borderColor: colors.grey[300],
        overflow: 'visible',
        opacity,
        position: 'relative',
        minHeight: '152px',
      }}
      elevation={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <Stack p={2} gap={2} height="100%">
        <Stack gap={1}>
          <Typography variant="h4" color={colors.grey[900]}>
            {name}
          </Typography>
          <Typography variant="caption" color={colors.grey[500]}>
            {typeName}
          </Typography>
        </Stack>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-end"
          flex={1}
          gap={1}
        >
          <Stack gap={0.5} height="100%">
            {shouldShowNoConversations ? (
              <Stack direction="row" alignItems="center" gap={1}>
                <Chat color={colors.grey[500]} />
                <Typography variant="body" color={colors.grey[700]}>
                  No conversations yet
                </Typography>
              </Stack>
            ) : (
              facts.map((fact) => {
                const factUi = getFactUi(agent.type, fact);
                if (!factUi) return null;
                if (fact.key === AgentFactKey.OBJECT_TYPE) {
                  // Check if this is a metadata type agent (has metadataType instead of objectType)
                  if (isMetadataTypeAgent(agent)) {
                    factUi.text = 'All Objects';
                  } else {
                    const objectType = fact.value as string;
                    factUi.text =
                      typeof objectType === 'string'
                        ? (objectTypesByName[objectType]?.label ?? objectType ?? '')
                        : '';
                  }
                }
                return (
                  <Stack
                    key={`${agent.type}-${fact.key}`}
                    direction="row"
                    alignItems="center"
                    gap={1}
                  >
                    {factUi.icon}
                    <Typography variant="body" color={colors.grey[700]}>
                      {factUi.text}
                    </Typography>
                  </Stack>
                );
              })
            )}
          </Stack>
          <Box
            sx={{
              whiteSpace: 'nowrap',
            }}
          >
            <Button variant="flat-blue" onClick={onClick} endIconName="ArrowRight">
              {ctaText}
            </Button>
          </Box>
        </Stack>
      </Stack>
      {!hideActionButtons && <AgentCardMenu agent={agent} isCardHovered={isHovered} />}
    </Card>
  );
};

export default AgentCard;
